import { ComponentManagerBase } from '@cof/fs-component-manager-base';
export class MaskedFieldManagerBase extends ComponentManagerBase {
    /**
     * Returns the current value of the managed input with the mask applied.
     */
    get maskedValue() {
        return this._maskValue(this._input.value);
    }
    /**
     * Returns the current value of the managed input without the mask removed.
     */
    get unmaskedValue() {
        return this._unmaskValue(this._input.value);
    }
    constructor(input, config) {
        super();
        /**
         * Configuration of the manager.
         */
        this._config = {};
        /**
         * Handle the user performing an "unknown" input type.
         * This is likely to be triggered on autocomplete.
         */
        this._handleInputUnknown = () => {
            const { selectionStart, selectionEnd, value } = this._input;
            const formattedValue = this._maskValue(value);
            this._input.value = formattedValue;
            const cursorPositionOffset = value.length - formattedValue.length;
            // As the true intent of this input type is unknowable, safety needs to be applied in case this input doesn't
            // involve the user being actively interacting with the input itself.
            if (selectionStart != null) {
                this._input.selectionStart = selectionStart - cursorPositionOffset;
            }
            if (selectionEnd != null) {
                this._input.selectionEnd = selectionEnd - cursorPositionOffset;
            }
        };
        /**
         * Handle the user pasting data into the input.
         */
        this._handleInsertFromPaste = () => {
            const { selectionStart, selectionEnd, value } = this._input;
            const cleanedValue = this._unmaskValue(value);
            const cursorPositionOffset = value.length - cleanedValue.length;
            this._input.value = cleanedValue;
            this._input.selectionStart = selectionStart - cursorPositionOffset;
            this._input.selectionEnd = selectionEnd - cursorPositionOffset;
        };
        /**
         * Gets an initial validity state object for the input that can be overwritten with custom validity rules.
         */
        this._getInitialValidityStateForInput = () => {
            const customValidity = {
                badInput: this._input.validity.badInput,
                customError: this._input.validity.customError,
                patternMismatch: this._input.validity.patternMismatch,
                rangeOverflow: this._input.validity.rangeOverflow,
                rangeUnderflow: this._input.validity.rangeUnderflow,
                stepMismatch: this._input.validity.stepMismatch,
                tooLong: this._input.validity.tooLong,
                tooShort: this._input.validity.tooShort,
                typeMismatch: this._input.validity.typeMismatch,
                valid: false /* Forced to false as this will be recalculated. */,
                valueMissing: this._input.validity.valueMissing
            };
            return customValidity;
        };
        /**
         * Handle the user blurring from the field.
         *
         * Mask the value of the field.
         */
        this._handleBlur = () => {
            this._input.value = this._maskValue(this._input.value);
        };
        /**
         * Manages which value is used when submitting a native HTML form. If useMaskedValue
         * is true, the field will use the masked value from the HTMLInputElement; otherwise,
         * the unmasked value will be used
         */
        this._handleFormData = ({ formData }) => {
            if (!this._config.useMaskedValue && this._input.name) {
                formData.set(this._input.name, this.unmaskedValue);
            }
        };
        /**
         * Handle the user inputting data.
         *
         * @param event input event triggered by the user interaction
         */
        this._handleInput = (event) => {
            switch (event.inputType) {
                case 'insertFromPaste': {
                    this._handleInsertFromPaste();
                    break;
                }
                case undefined: {
                    this._handleInputUnknown();
                    break;
                }
                default: {
                    break;
                }
            }
            this._dispatchMaskedFieldInputEvent();
        };
        /**
         * Handle the control being submitted in an invalid state by preventing the
         * default validation popup.
         *
         * @param event an event triggered when a field's validity is reported
         */
        this._handleInvalid = (event) => {
            /** Prevent the browser default validation popup */
            event.preventDefault();
            /** Focus the input if it is the first in invalid list of invalid controls */
            this._input.focus();
        };
        /**
         * Handle the user pressing a key while interacting with the input.
         * Prevent dissalowed keys from being entered.
         *
         * @param event an event created in reaction to the user pressing a key while interacting with the input
         */
        this._handleKeydown = (event) => {
            if (!this._isKeydownAllowed(event)) {
                event.preventDefault();
                return;
            }
        };
        this._input = input;
        this.updateConfig(config);
    }
    connect() {
        super.connect();
        this._input.addEventListener('blur', this._handleBlur, this.abortableEventListenerOptions);
        this._input.addEventListener('input', this._handleInput, this.abortableEventListenerOptions);
        this._input.addEventListener('invalid', this._handleInvalid, this.abortableEventListenerOptions);
        this._input.addEventListener('keydown', this._handleKeydown, this.abortableEventListenerOptions);
        if (this._input.form) {
            this._input.form.addEventListener('formdata', this._handleFormData, this.abortableEventListenerOptions);
        }
        // Format any value already present in the input.
        if (this._input.value) {
            this._input.value = this._maskValue(this._input.value);
        }
    }
    /**
     * Update the configuration of the manager.
     *
     * @param updatedConfig the updated configuration.
     */
    updateConfig(updatedConfig) {
        this._config = {
            ...this._config,
            ...updatedConfig
        };
    }
    /**
     * Returns whether a keydown event is allowed.
     *
     * @param event an event created in reaction to the user pressing a key while interacting with the input
     * @returns whether a keydown event is allowed
     */
    _isKeydownAllowed(event) {
        return (event.code === 'Backspace' ||
            event.code === 'Delete' ||
            event.code === 'Tab' ||
            event.code === 'Enter' ||
            event.code === 'Escape' ||
            event.code === 'ArrowLeft' ||
            event.code === 'ArrowRight' ||
            event.metaKey ||
            event.ctrlKey);
    }
}
//# sourceMappingURL=MaskedFieldManagerBase.js.map